import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import * as Sentry from '@sentry/angular-ivy';
import { ZkNotificationService } from '@zenklub/ng-ui';
import {
	INTERACTION_TYPE,
	ITEM_TYPE,
	SCOPE_TYPE,
	SUBSCOPE_TYPE,
} from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { SpeechToTextService } from 'app/services/speech-to-text.service';
import { DateTime } from 'luxon';
import { ServiceEvolutionQuality } from 'modules/promptuary/components/service-evolution-quality-banner/service-evolution-quality-banner.component';
import { IAppointment } from 'modules/video-conference/interfaces/appointment.interface';
import { DataBucket } from 'modules/video-conference/interfaces/databucket.interface';
import { DocumentaryRecordService } from 'modules/video-conference/services/documentary-record/documentary-record.service';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import * as secchiojs from 'secchiojs';
import { IAppointment as Appointment } from 'shared/interfaces/appointment.interface';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

@Component({
	selector: 'autosave-textarea',
	templateUrl: './autosave-textarea.component.html',
	styleUrls: ['./autosave-textarea.component.scss'],
	providers: [DocumentaryRecordService, PromptuaryService],
})
export class AutosaveTextarea implements OnInit, OnDestroy, OnChanges {
	charCount = 0;
	inputValue: string;
	inputValueSpeechToText = '';
	edited = false;
	lastEdited = DateTime.now();
	lastEditedMinutes = '0';
	status: 'default' | 'saved' | 'saving' | 'error' = 'default';
	minuteCounter: NodeJS.Timeout;
	secondsCounter: NodeJS.Timeout;
	deleteBucket: () => void;
	getBucket: () => DataBucket;
	updateBucket: (value: DataBucket) => void;
	speechToTextIsRecording = false;
	usedSpeechToText = false;

	@Input() initialValue: string;
	@Input() fetchInitialValue = true;
	@Input() formControl: FormControl;
	@Input() minLength;
	@Input() forceValue: string;
	@Input() helperTooltip = null;
	@Input() maxLength = 3000;
	@Input() appointment: IAppointment;
	@Input() placeholder: string;
	@Input() service: 'saveDocumentaryRecord' | 'autoSaveField';
	@Input() property: string;
	@Input() propertyPath: string[];
	@Input() title: string;
	@Input() canRecordWithSpeechToText = false;
	@Input() appointmentData: Appointment;
	@Input() rows?: number;
	@Input() quality: ServiceEvolutionQuality;
	@Input() showQuality: boolean;

	@Output('valueSaving') emValueSaving = new EventEmitter();
	@Output('valueChanged') emValueChanged =
		new EventEmitter<AutoSaveEventProps>();
	@Output('valueSaved') emValueSaved = new EventEmitter<AutoSaveEventProps>();
	@Output('usedSpeechToText') emUsedSpeechToText = new EventEmitter<boolean>();

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.forceValue && changes.forceValue.currentValue !== '') {
			this.inputValue = changes.forceValue.currentValue;
			this.autosaveText(this.inputValue);
		}
	}

	constructor(
		private documentaryRecordService: DocumentaryRecordService,
		private promptuaryService: PromptuaryService,
		private notificationService: ZkNotificationService,
		private speechToTextService: SpeechToTextService,
		public actionTrackerService: ActionTrackerService
	) {}

	ngOnInit(): void {
		this.inputValue =
			!this.fetchInitialValue && this.initialValue ? this.initialValue : '';
		this.updateCharCounter(this.inputValue);
		if (this.fetchInitialValue) {
			this.fetchRecords();
		}
		this.minuteCounter = setInterval(this.updateLastEditMsg.bind(this), 60000);
		if (this.canRecordWithSpeechToText) {
			this.speechToTextService.textChanged$.subscribe((text: string) => {
				if (this.charCount < this.maxLength) {
					this.inputValueSpeechToText = text;
					this.updateCharCounter(this.inputValue + this.inputValueSpeechToText);
				} else {
					this.inputValue = this.inputValue.slice(0, this.maxLength);
					this.updateCharCounter(this.inputValue);
				}
			});
		}
	}

	ngOnDestroy(): void {
		clearInterval(this.minuteCounter);
	}

	initLocalStorage(): void {
		[this.getBucket, this.updateBucket, this.deleteBucket] =
			secchiojs.getBucket<DataBucket>(this.property);

		const existingBucket = this.getBucket();

		if (existingBucket && !this.inputValue) {
			if (existingBucket.appointmentId != this.appointment.id) {
				return this.deleteBucket();
			}

			this.inputValue = existingBucket.value;
		}

		this.updateCharCounter(this.inputValue);
		this.emValueChanged.emit({
			newValue: this.inputValue,
			property: this.property,
		});

		this.emValueSaved.emit({
			property: this.property || true,
			newValue: this.inputValue,
		});
	}

	autosaveText(inputValue: string): void {
		this.status = 'saving';

		const service = this[this.service](inputValue);
		this.emValueSaving.emit();
		this.setBreadcrumb('starting autosave text', { property: this.property });
		service
			.pipe(
				finalize(() => {
					this.emValueSaved.emit({
						property: this.property || true,
						newValue: inputValue,
					});
				})
			)
			.subscribe(
				() => {
					this.status = 'saved';
					this.updateLastEditMsg();
					this.setBreadcrumb('autosave text complete', {
						property: this.property,
					});
				},
				({ error }) => {
					this.status = 'error';
					if (error.messageI18n) {
						return this.notificationService.showError(error.messageI18n);
					}
					throw Error(`Auto save error in property: ${this.property}.`);
				}
			);
	}

	fetchRecords() {
		if (this.service === 'autoSaveField') {
			this.getPromptuary().subscribe(
				(data) => {
					this.propertyPath = this.property.split('.');

					if (this.propertyPath.length === 2) {
						this.inputValue =
							data[this.propertyPath[0]][this.propertyPath[1]] || '';
					}

					if (this.propertyPath.length === 1) {
						this.inputValue = data[this.propertyPath[0]] || '';
					}

					this.initLocalStorage();
				},
				() => {
					console.error('Evolução do atendimento não encontrada.');
				}
			);
		}

		if (this.service === 'saveDocumentaryRecord') {
			this.getDocumentaryRecord().subscribe(
				(record) => {
					this.inputValue = record.value ? record.value : '';
					this.initLocalStorage();
				},
				() => {
					console.error('Registro documental não encontrado.');
				}
			);
		}
	}

	getPromptuary(): Observable<any> {
		return this.promptuaryService.getData(
			this.appointment.id,
			this.appointment.contact.id
		);
	}

	getPromptData(inputValue: string) {
		const promptuaryData = {
			eventId: this.appointment.id,
			customerId: this.appointment.contact.id,
		};

		this.propertyPath = this.property.split('.');

		if (this.propertyPath.length === 2) {
			promptuaryData[this.propertyPath[0]] = new Object();
			promptuaryData[this.propertyPath[0]][this.propertyPath[1]] = inputValue;
		}

		if (this.propertyPath.length === 1) {
			promptuaryData[this.propertyPath[0]] = inputValue;
		}

		return promptuaryData;
	}

	autoSaveField(inputValue: string): Observable<any> {
		const promptuaryData = this.getPromptData(inputValue);

		return this.promptuaryService.autoSaveField(promptuaryData);
	}

	getDocumentaryRecord(): Observable<any> {
		return this.documentaryRecordService.getData(this.appointment.id);
	}

	saveDocumentaryRecord(inputValue: string): Observable<any> {
		return this.documentaryRecordService.saveDocumentaryRecord({
			value: inputValue,
			eventId: this.appointment.id,
			customerId: this.appointment.contact.id,
		});
	}

	updateText(newValue: string): void {
		this.emValueChanged.emit({ newValue, property: this.property });
		clearInterval(this.secondsCounter);

		this.edited = true;
		this.lastEdited = DateTime.now();
		this.secondsCounter = setInterval(() => {
			this.autosaveText(newValue);
			clearInterval(this.secondsCounter);
		}, 1400);

		if (this.updateBucket) {
			this.updateBucket({
				appointmentId: this.appointment.id,
				value: newValue,
			});
		}
		this.updateCharCounter(newValue);
		if (this.formControl) {
			this.formControl.setValue(newValue);
		}
	}

	updateCharCounter(newValue: string): void {
		this.charCount = newValue?.length || 0;
	}

	updateLastEditMsg(): void {
		const { minutes: minutesDiff } = DateTime.now()
			.diff(this.lastEdited, 'minutes')
			.toObject();

		this.lastEditedMinutes = Math.floor(minutesDiff).toString();
	}

	recordingSpeechToText(isRecording: boolean) {
		this.speechToTextIsRecording = isRecording;
		if (isRecording) {
			this.usedSpeechToText = true;
			this.emUsedSpeechToText.emit(true);
			this.actionTrackerService.eventTracker(
				SCOPE_TYPE.Promptuary,
				SUBSCOPE_TYPE.SpeechToText,
				ITEM_TYPE.StartSpeechToTextBtn,
				INTERACTION_TYPE.Click,
				{
					eventId: this.appointmentData.id,
					professionalId: this.appointmentData.professionalId,
				}
			);
		} else {
			this.updateText(
				this.inputValue?.length
					? this.inputValue + '\n' + this.inputValueSpeechToText
					: this.inputValueSpeechToText
			);
		}
	}

	setBreadcrumb(message, property?) {
		Sentry.addBreadcrumb({
			category: 'ui.autosave-textarea',
			message: message,
			data: property ?? {},
			level: 'info',
			type: 'default',
		});
	}
}

export interface AutoSaveEventProps {
	newValue: string;
	property?: string | boolean;
}
